import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { reportsApi, branchesApi } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Search, Download, Building2 } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export default function StudiesReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await branchesApi.getAll();
      setBranches(response.data);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getStudiesSummary(
        startDate || undefined,
        endDate || undefined,
        selectedBranch || undefined
      );
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar reporte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const chartData = data?.studies?.slice(0, 10).map((s) => ({
    name: s.name.length > 15 ? s.name.slice(0, 15) + "..." : s.name,
    cantidad: s.count,
    ingresos: s.revenue,
  })) || [];

  return (
    <div className="space-y-6" data-testid="studies-report-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
          Reporte de Estudios
        </h1>
        <p className="text-muted-foreground">
          Estadísticas de estudios realizados
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Sucursal</Label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="w-48" data-testid="branch-select">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Seleccionar sucursal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las sucursales</SelectItem>
                  {branches.map((branch) => (
                    <SelectItem key={typeof branch === 'string' ? branch : branch.id} value={typeof branch === 'string' ? branch : branch.name}>
                      {typeof branch === 'string' ? branch : branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fecha Inicio</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha Fin</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date"
              />
            </div>
            <Button onClick={fetchReport} disabled={loading} data-testid="filter-btn">
              <Search className="h-4 w-4 mr-2" />
              {loading ? "Cargando..." : "Filtrar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="text-sm text-muted-foreground">Total Órdenes</div>
                <div className="text-3xl font-bold font-['Manrope']">
                  {data.total_orders.toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-sm text-muted-foreground">
                  Ingresos Totales
                </div>
                <div className="text-3xl font-bold font-['Manrope'] text-emerald-600">
                  ${data.total_revenue.toLocaleString()}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Top 10 Estudios por Cantidad
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={120} />
                      <Tooltip />
                      <Bar dataKey="cantidad" fill="#0284C7" radius={4} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Detalle por Estudio</CardTitle>
            </CardHeader>
            <CardContent>
              {data.studies.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No hay datos para mostrar
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Estudio</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead className="text-right">Ingresos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.studies.map((study, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{study.name}</TableCell>
                        <TableCell className="text-right">{study.count}</TableCell>
                        <TableCell className="text-right">
                          ${study.revenue.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
